import React from 'react';
import styled, { css } from 'styled-components';
import { useImageComponent, useLinkComponent } from '../../utils/component';
import { ImageProps } from '../../atoms/types';
import { useTranslation } from '../../utils/translation';
import { Cta } from '../cta';
import { Stars } from '../stars';
import { DEVICE_SIZE, mq } from '../../utils/mq';

const Wrapper = styled.div`
  border-radius: var(--br-s);
  background-color: var(--color-alt-4);
  box-shadow: 0px 0px 30px 0px var(--color-box-shadow);
  margin: var(--spacer-fluid) 0 var(--spacer);
  padding: var(--spacer-fluid) var(--spacer-m-to-s);
  top: 0;
  transition: top 0.3s;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: var(--spacer-s) -60px var(--spacer);
    `,
  )}
`;

const InsertBody = styled.div`
  picture {
    width: 100%;
    height: auto;
  }
`;

const Notice = styled.p`
  font-size: 13px;
  margin: var(--spacer-xs) 0 0;
  color: var(--color-alt-1);
`;

const WrapperCta = styled.p`
  text-align: center;
`;
const HeadInsert = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacer-xs);
  align-items: center;
  padding-bottom: var(--spacer-xs);
`;

const DownloadHeaderRating = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  text-align: center;
  svg {
    vertical-align: middle;
  }
  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      align-items: center;
      flex-direction: row-reverse;
      gap: var(--spacer-xs);
    `,
  )}
`;

const RatingValue = styled.span`
  font-family: var(--theme-typography-primary-font);
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  vertical-align: middle;
  margin-right: var(--spacer-xs);
  ${mq.gte(
    DEVICE_SIZE.MEDIUM,
    css`
      font-size: 20px;
      font-weight: bold;
      line-height: 28px;
    `,
  )}
`;

const WrapperStars = styled.div`
  display: flex;
  flex-direction: row;
  flex-flow: nowrap;
`;

const TitleInsert = styled.span`
  font-family: var(--theme-typography-primary-font);
  font-size: 20px;
  font-weight: bold;
  line-height: 28px;
  margin: var(--spacer-s) 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 22px;
    `,
  )}
`;

const TitleBody = styled.span`
  font-family: var(--theme-typography-primary-font);
  font-size: 20px;
  font-weight: bold;
  display: block;
  line-height: 28px;
  padding-top: var(--spacer-s);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 20px;
    `,
  )}
`;

const DescriptionBody = styled.p`
  padding: var(--spacer-s) 0;
`;

type FeaturedItem = {
  id: string;
  title: string;
  description: string;
  image: string | number;
  url: string;
  globalScore: number;
};

type Overrides = {
  validPage: string;
  title: string;
  shortTitle: string;
  image: string;
  url: string;
  description: string;
};

type TemplateConfig = {
  id: string;
};

type Item = {
  PLUGIN_NAME: string;
  validPage: string;
  templateConfigPath: string[];
  templateConfig: TemplateConfig;
  item: FeaturedItem;
};

export type InsertProps = {
  overrides: Overrides;
  item: Item;
};

export const Insert = ({ overrides, item: { item } }: InsertProps) => {
  const t = useTranslation();
  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();
  const bigImageProps: ImageProps = {
    imageId: overrides.image,
    alt: overrides.title,
    mobileWidth: 500,
    mobileHeight: 300,
    desktopWidth: 1000,
    desktopHeight: 400,
  };

  if (!item) {
    return null;
  }

  const logoImageProps: ImageProps = item?.image
    ? {
        imageId: `${item.image}`,
        alt: item.title,
        mobileWidth: 42,
        mobileHeight: 42,
        desktopWidth: 42,
        desktopHeight: 42,
      }
    : null;

  return (
    <Wrapper>
      <HeadInsert>
        <ImageComponent {...logoImageProps} />
        <TitleInsert>{item.title}</TitleInsert>
        <DownloadHeaderRating>
          <RatingValue>{item?.globalScore ?? 0}/10</RatingValue>
          <WrapperStars>
            <Stars max={5} rating={item?.globalScore / 2 ?? 0} />
          </WrapperStars>
        </DownloadHeaderRating>
      </HeadInsert>

      <InsertBody>
        <LinkComponent href={overrides.url} rel={['nofollow']} openInNewWindow>
          <ImageComponent {...bigImageProps} />
        </LinkComponent>
        <TitleBody>{overrides.title}</TitleBody>
        <DescriptionBody>{overrides.description}</DescriptionBody>
        <WrapperCta>
          <Cta href={overrides.url} rel={['nofollow']} openInNewWindow className="mod-large">
            {overrides.shortTitle ?? t('design.article.insert.cta')}
          </Cta>
          <Notice>{t('design.article.insert.sponsoLabel')}</Notice>
        </WrapperCta>
      </InsertBody>
    </Wrapper>
  );
};
